import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { cn } from "@repo/blacksmith-ui/cn";

/** Row height for skeleton tree rows (matches TestList virtualization). */
const ROW_HEIGHT_SUITE = 42;
const SKELETON_ROW_COUNT = 6;
const BORDER_SUBTLE = "border-black/[0.08] dark:border-white/[0.08]";

/**
 * Skeleton for the Tests tab content (two-pane: test list + detail pane).
 * Used when TestList is loading and when Run Details is loading with tab=tests.
 * Structure must match TestList exactly to avoid layout shift (root + wrapper + card).
 */
export function TestListSkeleton() {
  return (
    <div className="flex flex-col flex-1 min-h-full overflow-x-hidden bg-app-background">
      <div
        className="flex mt-6 mb-6 ml-6 mr-2 overflow-hidden"
        style={{
          width: "calc(100% - 48px)",
          height: "calc(100vh - 162px)",
        }}
      >
        <div className="flex w-full bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] dark:border-[var(--color-border)] shadow-sm overflow-hidden min-h-0">
          {/* Left: test list - matches Filter & Search header + optional badges + tree */}
          <div
            className={cn(
              "flex flex-col min-h-0 flex-shrink-0 w-[260px] min-w-[260px] border-r",
              BORDER_SUBTLE,
            )}
          >
            <div
              className={cn(
                "flex items-center gap-3 h-[42px] px-4 border-b",
                BORDER_SUBTLE,
              )}
            >
              <Skeleton className="w-[26px] h-[26px] rounded-lg shrink-0" />
              <Skeleton className="w-[26px] h-[26px] rounded-lg shrink-0" />
              <Skeleton className="h-[26px] flex-1 max-w-[140px] rounded-lg" />
            </div>
            <div className={cn("px-4 py-2 border-b", BORDER_SUBTLE)}>
              <Skeleton className="h-5 w-24 rounded-md" />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col py-1">
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => {
                const isSuiteRow = i === 0;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-4 shrink-0"
                    style={{ height: ROW_HEIGHT_SUITE }}
                  >
                    {isSuiteRow ? (
                      <>
                        <Skeleton className="w-4 h-4 shrink-0 rounded" />
                        <Skeleton className="h-4 flex-1 max-w-[120px] rounded" />
                        <Skeleton className="h-5 w-16 rounded-md shrink-0" />
                      </>
                    ) : (
                      <>
                        <div className="w-5 shrink-0" />
                        <Skeleton className="w-4 h-4 shrink-0 rounded-full" />
                        <Skeleton
                          className="h-4 flex-1 rounded"
                          style={{
                            maxWidth: i === 1 ? "180px" : "160px",
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Right: detail pane - matches TestDetailHeader + TestHistoryPanel */}
          <div className="flex-1 min-h-0 flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-4 px-4 h-[42px] border-b border-[var(--color-border)] dark:border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
              <div className="flex items-center gap-4">
                <Skeleton className="h-[22px] w-16 rounded-md" />
                <Skeleton className="h-[22px] w-12 rounded-md" />
                <Skeleton className="h-7 w-24 rounded-md" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-7 w-8 rounded-md" />
                <Skeleton className="h-7 w-8 rounded-md" />
                <Skeleton className="h-7 w-10 rounded-md" />
                <Skeleton className="h-7 w-8 rounded-md" />
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-4 py-4 gap-6">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 flex-shrink-0">
                <Skeleton className="h-4 flex-1 max-w-[320px] rounded" />
                <Skeleton className="w-5 h-5 shrink-0 rounded" />
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Skeleton className="h-4 w-20 rounded" />
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 space-y-2">
                  <Skeleton className="h-3 w-full rounded" />
                  <Skeleton className="h-3 w-[90%] rounded" />
                  <Skeleton className="h-3 w-[70%] rounded" />
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Skeleton className="h-4 w-24 rounded" />
                <Skeleton className="h-[363px] w-full rounded-lg" />
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <Skeleton className="h-4 w-28 rounded" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-28 rounded-md" />
                  <Skeleton className="h-9 w-24 rounded-md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
